import { useEffect, useState } from 'react';
import { competitionsService } from '../../../services/competitions';
import { cragsService } from '../../../services/crags';
import { gymsService } from '../../../services/gyms';
import type { GradingSystemWithGrades } from '../../../types/gym';
import type { ClimbEditorPlace, ClimbEditorType } from './useClimbEditorRoute';

/** As much of a place as this page needs: who it is, and how it grades. */
export interface ClimbEditorLocation {
  id: string;
  name: string;
  slug: string;
  logoUrl?: string | null;
  boulderGrades: GradingSystemWithGrades | null;
  routeGrades: GradingSystemWithGrades | null;
}

/**
 * The place a climb is being put up at.
 *
 * Each kind answers for itself — a gym, a crag and a competition each have their
 * own endpoint and each carries its **resolved** grading systems, ladder and
 * all. That matters: the old two-step form looked a location up through
 * `searchService`, which asks Elasticsearch for a record whose slug is already
 * in the URL, and answers with whatever was last indexed.
 */
export function useClimbEditorLocation(place: ClimbEditorPlace, slug: string | undefined) {
  const [location, setLocation] = useState<ClimbEditorLocation | null>(null);
  const [loading, setLoading] = useState(!!slug && place !== 'board');

  useEffect(() => {
    // A board problem hangs off the board, which the page fetches for itself.
    if (!slug || place === 'board') return;

    let cancelled = false;
    setLoading(true);
    fetchPlace(place, slug)
      .then((loaded) => {
        if (!cancelled) setLocation(loaded);
      })
      .catch(() => {
        if (!cancelled) setLocation(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [place, slug]);

  return { location, loading };
}

/** The ladder a climb of this type is graded on here, or an empty one. */
export function gradesFor(
  location: ClimbEditorLocation | null,
  type: ClimbEditorType
): GradingSystemWithGrades['grades'] {
  const system = type === 'Route' ? location?.routeGrades : location?.boulderGrades;

  // Ids as strings, whatever the payload said. A gym serialises its ladder with
  // numeric grade ids and a climb serialises `grade_id` as a string, and the two
  // meet on the wheel, which matches by identity — so a climb opened for editing
  // never found its own grade (`42 === '42'` is false) and the field read as if
  // nothing had been set. One shape for both, at the one place the page reads a
  // ladder.
  return (system?.grades ?? []).map((grade) => ({ ...grade, id: String(grade.id) }));
}

async function fetchPlace(
  place: ClimbEditorPlace,
  slug: string
): Promise<ClimbEditorLocation | null> {
  if (place === 'crag') {
    const crag = await cragsService.getCragBySlug(slug);
    return {
      id: crag.id,
      name: crag.name,
      slug: crag.slug,
      logoUrl: crag.logo_small_url ?? crag.logo_url,
      boulderGrades: crag.boulder_grading_system ?? null,
      routeGrades: crag.route_grading_system ?? null,
    };
  }

  if (place === 'competition') {
    const competition = await competitionsService.getCompetitionBySlug(slug);
    return {
      id: competition.id,
      name: competition.name,
      slug: competition.slug,
      logoUrl: competition.logo_small_url ?? competition.logo_url,
      boulderGrades: competition.boulder_grading_system ?? null,
      routeGrades: competition.route_grading_system ?? null,
    };
  }

  const gym = await gymsService.getGymBySlug(slug);
  return {
    id: gym.id,
    name: gym.name,
    slug: gym.slug,
    logoUrl: gym.logo_small_url ?? gym.logo_url,
    boulderGrades: gym.boulder_grading_system ?? null,
    routeGrades: gym.route_grading_system ?? null,
  };
}
